// myapp/src/context/AppAuthContext.tsx
import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import appApi from '../api/appApi';
import { User } from '../types';

interface AppAuthContextType {
  user: User | null;
  role: string | null;
  permissions: string[];
  companyId: number | null;
  loading: boolean;
  login: (credentials: any) => Promise<any>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  isAuthenticated: boolean;
  theme: string;
  toggleTheme: () => void;
}

const AppAuthContext = createContext<AppAuthContextType | undefined>(undefined);

export const useAppAuth = (): AppAuthContextType => {
  const context = useContext(AppAuthContext);
  if (!context) throw new Error('useAppAuth must be used within an AppAuthProvider');
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

// Lightweight native JWT parser
const parseJwt = (token: string) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
};

export const AppAuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [companyId, setCompanyId] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  // Theme configuration
  const [theme, setTheme] = useState<string>(localStorage.getItem('theme') || 'dark'); // Default to beautiful premium dark mode

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    document.documentElement.setAttribute('data-theme', newTheme);
  };

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  // Session Verification
  const checkAuth = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('app_jwt_token');
      if (!token) {
        setUser(null);
        setRole(null);
        setPermissions([]);
        setCompanyId(null);
        return;
      }

      // Decode JWT locally for instantaneous claims mapping
      const claims = parseJwt(token);
      if (claims) {
        const parsedRole = claims["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"] || claims["Role"] || null;
        setRole(parsedRole);
        setCompanyId(claims["CompanyId"] ? parseInt(claims["CompanyId"]) : null);
        const permsStr = claims["Permissions"] || '';
        setPermissions(permsStr ? permsStr.split(',') : []);
      }

      // Call API context lookup
      const res = await appApi.get('/Auth/me');
      setUser(res.data);
      setRole(res.data.roleName);
      setCompanyId(res.data.companyId || null);
    } catch (err) {
      console.warn("App auth lookup failed, clearing expired session tokens.", err);
      localStorage.removeItem('app_jwt_token');
      setUser(null);
      setRole(null);
      setPermissions([]);
      setCompanyId(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initialize session on startup
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // App login request
  const login = async (credentials: any) => {
    const payload = {
      ...credentials,
      portalType: 'app'
    };

    const res = await appApi.post('/Auth/login', payload);
    const token = res.data.token;
    localStorage.setItem('app_jwt_token', token);

    // Decode claims
    const claims = parseJwt(token);
    if (claims) {
      const parsedRole = claims["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"] || claims["Role"] || null;
      setRole(parsedRole);
      setCompanyId(claims["CompanyId"] ? parseInt(claims["CompanyId"]) : null);
      const permsStr = claims["Permissions"] || '';
      setPermissions(permsStr ? permsStr.split(',') : []);
    }

    setUser(res.data.user);
    return res.data;
  };

  // App session teardown
  const logout = async () => {
    try {
      await appApi.post('/Auth/logout', {});
    } catch (e) {
      // Ignore API logout errors
    } finally {
      localStorage.removeItem('app_jwt_token');
      setUser(null);
      setRole(null);
      setPermissions([]);
      setCompanyId(null);

      // Redirect strictly to app login screen on active domain
      window.location.href = '/login';
    }
  };

  const value: AppAuthContextType = {
    user,
    role,
    permissions,
    companyId,
    loading,
    login,
    logout,
    checkAuth,
    isAuthenticated: !!user,
    theme,
    toggleTheme,
  };

  return <AppAuthContext.Provider value={value}>{children}</AppAuthContext.Provider>;
};
